import { useEffect, useState } from "react";
import { X, Zap, Coins, CreditCard, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * BoostDialog — modal where a provider picks a tier (24h / 3d / 7d) and a
 * payment method ($USD via Stripe or $CAUL from their wallet) to boost one
 * of their services to the top of Marketplace results.
 *
 * Renders as `null` when `open=false`. Caller passes the service object;
 * close on success or X click.
 */
export default function BoostDialog({ service, open, initialTierId, onClose, onBoosted }) {
  const [tiers, setTiers] = useState([]);
  const [tierId, setTierId] = useState(initialTierId || "boost_24h_5");
  const [method, setMethod] = useState("usd");
  const [submitting, setSubmitting] = useState(false);
  const [caulBalance, setCaulBalance] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (initialTierId) setTierId(initialTierId);
    let cancelled = false;
    (async () => {
      try {
        const [t, w] = await Promise.all([
          api.get("/boosts/tiers"),
          api.get("/wallet"),
        ]);
        if (cancelled) return;
        setTiers(t.data);
        setCaulBalance(w.data?.wallet?.balance ?? 0);
      } catch (e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [open, initialTierId]);

  if (!open) return null;
  const tier = tiers.find((t) => t.id === tierId) || tiers[0];

  const insufficient =
    method === "caul" && tier && (caulBalance ?? 0) < tier.caul;

  const submit = async () => {
    if (!service || !tier) return;
    setSubmitting(true);
    try {
      if (method === "usd") {
        const { data } = await api.post("/boosts/checkout", {
          serviceId: service.id,
          tierId: tier.id,
          origin: window.location.origin,
        });
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        toast.error("Could not start checkout");
      } else {
        const { data } = await api.post("/boosts/redeem-caul", {
          serviceId: service.id,
          tierId: tier.id,
        });
        toast.success("Boost activated — your listing is now featured");
        onBoosted?.(data.boost);
        onClose();
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not activate boost");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="boost-dialog"
    >
      <div
        className="parchment max-w-md w-full p-6 space-y-5 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-3 right-3 text-foreground/40 hover:text-foreground"
          onClick={onClose}
          data-testid="boost-dialog-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div>
          <div className="flex items-center gap-2 mb-1">
            <Zap size={16} className="text-secondary" strokeWidth={2} />
            <span className="label-mystic text-secondary">Boost listing</span>
          </div>
          <h3 className="font-serif text-2xl">{service?.title || "Service"}</h3>
          <p className="text-xs text-foreground/55 mt-1">
            Pin this service to the top of Marketplace category results.
            Transparently labeled as "Boosted" — same trust scores still apply.
          </p>
        </div>

        {/* Tier selector */}
        <div className="space-y-2">
          <div className="label-mystic text-foreground/55">Duration</div>
          <div className="grid grid-cols-3 gap-2">
            {tiers.map((t) => {
              const active = t.id === tierId;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTierId(t.id)}
                  className={`p-3 rounded-sm border text-left transition-colors ${
                    active
                      ? "border-secondary bg-secondary/10"
                      : "border-foreground/15 hover:border-foreground/35"
                  }`}
                  data-testid={`boost-tier-${t.id}`}
                >
                  <div className={`font-serif text-base ${active ? "text-secondary" : "text-foreground"}`}>
                    {t.hours < 48 ? `${t.hours}h` : `${Math.round(t.hours / 24)}d`}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-foreground/55 mt-1">
                    ${t.usd} · {t.caul} CAUL
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Method selector */}
        <div className="space-y-2">
          <div className="label-mystic text-foreground/55">Pay with</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMethod("usd")}
              className={`p-3 rounded-sm border flex items-center gap-2 transition-colors ${
                method === "usd"
                  ? "border-primary bg-primary/10"
                  : "border-foreground/15 hover:border-foreground/35"
              }`}
              data-testid="boost-pay-usd"
            >
              <CreditCard size={14} />
              <span className="text-sm">USD · ${tier?.usd ?? "—"}</span>
            </button>
            <button
              type="button"
              onClick={() => setMethod("caul")}
              className={`p-3 rounded-sm border flex items-center gap-2 transition-colors ${
                method === "caul"
                  ? "border-primary bg-primary/10"
                  : "border-foreground/15 hover:border-foreground/35"
              }`}
              data-testid="boost-pay-caul"
            >
              <Coins size={14} />
              <span className="text-sm">$CAUL · {tier?.caul ?? "—"}</span>
            </button>
          </div>
          {method === "caul" && caulBalance !== null && (
            <div className={`text-[11px] ${insufficient ? "text-destructive" : "text-foreground/55"}`}>
              Wallet balance: {caulBalance.toLocaleString()} CAUL
              {insufficient && " — top up before boosting."}
            </div>
          )}
        </div>

        <button
          type="button"
          disabled={submitting || !tier || insufficient}
          onClick={submit}
          className="w-full px-5 py-3 rounded-sm bg-secondary text-secondary-foreground hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-[0.22em] font-medium flex items-center justify-center gap-2"
          data-testid="boost-confirm-btn"
        >
          <Sparkles size={13} />
          {submitting
            ? "Activating…"
            : method === "usd"
              ? `Boost for $${tier?.usd ?? ""}`
              : `Boost for ${tier?.caul ?? ""} CAUL`}
        </button>
      </div>
    </div>
  );
}
